import { instanceRef } from '../utils/axios';

// ========================
// Transformadores snake_case → camelCase
// ========================
const snakeToCamel = (str) =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

const camelToSnake = (str) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

const toSnakeKeys = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(toSnakeKeys);
  if (typeof obj !== 'object' || obj instanceof Date) return obj;

  return Object.keys(obj).reduce((acc, key) => {
    acc[camelToSnake(key)] = toSnakeKeys(obj[key]);
    return acc;
  }, {});
};

const transformKeys = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map(transformKeys);
  if (typeof obj !== 'object' || obj instanceof Date) return obj;

  return Object.keys(obj).reduce((acc, key) => {
    acc[snakeToCamel(key)] = transformKeys(obj[key]);
    return acc;
  }, {});
};

// Transforma un caso de la API al formato que espera el frontend
const transformCaso = (raw) => {
  if (!raw) return null;
  const c = transformKeys(raw);

  // vehiculo viene como relación del backend (tabla casos_vehiculo)
  if (!c.vehiculo) {
    c.vehiculo = {};
  }

  // Aplanar campos del vehículo al nivel superior (caso.chapa, caso.marca, etc.)
  c.chapa = c.vehiculo.chapa || '';
  c.chassis = c.vehiculo.chassis || '';
  c.marca = c.vehiculo.marca || '';
  c.modelo = c.vehiculo.modelo || '';
  c.anio = c.vehiculo.anio || null;
  c.color = c.vehiculo.color || '';

  // Mapear monto_estimado → monto (nombre usado en frontend)
  if (c.montoEstimado !== undefined) {
    c.monto = c.montoEstimado;
  }

  // Aplanar relaciones para display en listas
  // taller: objeto → mantener objeto pero agregar nombre plano
  if (c.taller && typeof c.taller === 'object') {
    c.tallerObj = c.taller;
    c.taller = c.taller.nombre || '';
  }

  // aseguradora: extraer nombre y código de la relación
  c.aseguradora = c.aseguradoraRelacion?.nombre || '';
  c.aseguradoraCodigo = c.aseguradoraRelacion?.codigo || '';

  // asegurado: objeto → mantener objeto pero agregar nombre plano
  if (c.asegurado && typeof c.asegurado === 'object') {
    c.aseguradoObj = c.asegurado;
    c.asegurado = c.asegurado.nombreCompleto || '';
  }

  // poliza: objeto → mantener objeto pero agregar numero plano
  if (c.poliza && typeof c.poliza === 'object') {
    c.polizaObj = c.poliza;
    c.poliza = c.poliza.numero || '';
  }

  // perito: extraer nombre de la relación
  if (c.perito && typeof c.perito === 'object') {
    c.peritoNombre = c.perito.nombres || c.perito.usuario || '';
  }
  // Mapear presupuestoActivo → campos raíz para backward compat
  const presupuestoActivo = c.presupuestoActivo ?? null;
  c.manoObraGestionada = presupuestoActivo?.idGestorMo != null;
  c.idGestorMo = presupuestoActivo?.idGestorMo ?? null;
  c.idAprobadorMo = presupuestoActivo?.idAprobadorMo ?? null;
  c.numeroPresupuestoActual = presupuestoActivo?.numeroPresupuesto ?? 0;
  c.comentariosPresupuesto = presupuestoActivo?.comentariosPresupuesto ?? null;
  c.gestorMo = presupuestoActivo?.gestorMo ?? null;
  c.aprobadorMo = presupuestoActivo?.aprobadorMo ?? null;
  c.itemsManoObra = presupuestoActivo?.itemsManoObra ?? [];
  c.itemsRepuestos = presupuestoActivo?.itemsRepuestos ?? [];

  // Aplanar ordenes desde todos los presupuestos (con badge de presupuesto para la UI)
  if (!c.ordenes || c.ordenes.length === 0) {
    const ordenesFromPresupuestos = (c.presupuestos || []).flatMap((p) =>
      (p.ordenes || []).map((o) => ({
        ...o,
        numeroPresupuesto: p.numeroPresupuesto,
        // numeroPedido (alias usado en la UI) = número de presupuesto al que
        // pertenece la orden. Si > 0 es una orden de solicitud adicional.
        numeroPedido: o.numeroPedido ?? p.numeroPresupuesto ?? 0,
        // esAdicional: derivado del presupuesto/licitación. Una orden creada
        // dentro del flujo de solicitud adicional debe figurar como adicional.
        esAdicional:
          o.esAdicional ?? p.esAdicional ?? (p.numeroPresupuesto || 0) > 0
      }))
    );
    if (ordenesFromPresupuestos.length > 0) c.ordenes = ordenesFromPresupuestos;
  }

  // Aplanar licitaciones desde presupuestoActivo y todos los presupuestos, deduplicando por id
  if (!c.licitaciones || c.licitaciones.length === 0) {
    const allLits = [
      ...(presupuestoActivo?.licitaciones || []),
      ...(c.presupuestos || []).flatMap((p) => p.licitaciones || [])
    ];
    const uniqueLits = [];
    const seenIds = new Set();
    for (const lit of allLits) {
      if (lit && lit.id && !seenIds.has(lit.id)) {
        seenIds.add(lit.id);
        uniqueLits.push(lit);
      }
    }
    if (uniqueLits.length > 0) c.licitaciones = uniqueLits;
  }

  // licitacionGestionada: derivado del estado de licitaciones (campo eliminado de casos_caso)
  c.licitacionGestionada = (c.licitaciones || []).some(
    (l) => l.gestionada || l.estadoLicitacion === 'gestionada'
  );

  // Una orden es de repuestos si:
  //   - tiene proveedor (id o objeto con id), O
  //   - tiene licitación asociada (idLicitacion)
  // Una orden es de mano de obra si NO tiene proveedor NI licitación
  if (c.ordenes && Array.isArray(c.ordenes)) {
    c.ordenes = c.ordenes.map((orden) => {
      // Verificar si tiene proveedor de forma más estricta
      const tieneProveedor =
        orden.idProveedor ||
        (orden.proveedor &&
          typeof orden.proveedor === 'object' &&
          orden.proveedor.id);

      // Verificar si tiene licitación (indica que es orden de repuestos)
      const tieneLicitacion =
        orden.idLicitacion ||
        (orden.licitacion &&
          typeof orden.licitacion === 'object' &&
          orden.licitacion.id);

      // Es orden de repuestos si tiene proveedor O licitación
      const esOrdenRepuestos = tieneProveedor || tieneLicitacion;

      // Resolver numeroPedido/esAdicional desde fuentes posibles, en orden:
      //   1. El que ya viene en la orden (set arriba al aplanar presupuestos)
      //   2. El presupuesto anidado de la orden (cuando viene incluido)
      //   3. La licitación anidada (caso repuestos)
      //   4. Default 0 / false (original)
      const numeroPedidoResuelto =
        orden.numeroPedido ??
        orden.numeroPresupuesto ??
        orden.presupuesto?.numeroPresupuesto ??
        orden.licitacion?.numeroPedido ??
        0;
      const esAdicionalResuelto =
        orden.esAdicional ??
        orden.presupuesto?.esAdicional ??
        orden.licitacion?.esAdicional ??
        numeroPedidoResuelto > 0;

      const baseOrden = {
        ...orden,
        numeroPedido: numeroPedidoResuelto,
        esAdicional: esAdicionalResuelto
      };

      if (esOrdenRepuestos) {
        return {
          ...baseOrden,
          itemsRepuestos: orden.items || [],
          itemsManoObra: [],
          proveedorObj:
            orden.proveedor && typeof orden.proveedor === 'object'
              ? orden.proveedor
              : null,
          proveedor:
            orden.proveedor && typeof orden.proveedor === 'object'
              ? orden.proveedor.nombre
              : orden.proveedor || null
        };
      }
      return {
        ...baseOrden,
        itemsManoObra: orden.items || [],
        itemsRepuestos: []
      };
    });
  }

  return c;
};

const transformTaller = (raw) => {
  if (!raw) return null;
  const t = transformKeys(raw);

  // Aplanar especialidades de [{especialidad: 'X'}] → ['X']
  if (t.especialidades && Array.isArray(t.especialidades)) {
    t.especialidades = t.especialidades.map((e) =>
      typeof e === 'object' ? e.especialidad : e
    );
  }

  // Aplanar certificaciones de [{certificacion: 'X', ...}] → ['X'] para lista
  if (t.certificaciones && Array.isArray(t.certificaciones)) {
    t.certificacionesObj = t.certificaciones;
    t.certificaciones = t.certificaciones.map((c) =>
      typeof c === 'object' ? c.certificacion : c
    );
  }

  return t;
};

const transformProveedor = (raw) => {
  if (!raw) return null;
  const p = transformKeys(raw);

  // Aplanar tiposRepuesto
  if (p.tiposRepuesto && Array.isArray(p.tiposRepuesto)) {
    p.tipoRepuestos = p.tiposRepuesto.map((t) =>
      typeof t === 'object' ? t.tipoRepuesto : t
    );
  }

  // Aplanar marcas
  if (p.marcas && Array.isArray(p.marcas)) {
    p.marcasAtendidas = p.marcas.map((m) =>
      typeof m === 'object' ? m.marca : m
    );
  }

  // Aplanar metodosPago
  if (p.metodosPago && Array.isArray(p.metodosPago)) {
    p.metodoPago = p.metodosPago.map((m) =>
      typeof m === 'object' ? m.metodoPago : m
    );
  }

  return p;
};

// ========================
// Casos
// ========================
export const casosApi = {
  getAll: (filters = {}) =>
    instanceRef.instance.get('casos', { params: filters }).then((res) => ({
      data: (res.data.data || []).map(transformCaso),
      pagination: res.data.pagination || {
        page: 1,
        pageSize: 24,
        total: res.data.data?.length || 0,
        totalPages: 1
      }
    })),

  getById: (id) =>
    instanceRef.instance
      .get(`casos/${id}`)
      .then((res) => transformCaso(res.data.data)),

  getCompleto: (id) =>
    instanceRef.instance
      .get(`casos/${id}/completo`)
      .then((res) => transformCaso(res.data.data)),

  getDetalle: (id) =>
    instanceRef.instance
      .get(`casos/${id}/detalle`)
      .then((res) => transformCaso(res.data.data)),

  getPresupuestoDatos: (id) =>
    instanceRef.instance
      .get(`casos/${id}/presupuesto-datos`)
      .then((res) => transformCaso(res.data.data)),

  create: (data) =>
    instanceRef.instance
      .post('casos', toSnakeKeys(data))
      .then((res) => transformCaso(res.data.data)),

  derivacionManual: (data) =>
    instanceRef.instance
      .post('casos/derivacion-manual', data)
      .then((res) => transformCaso(res.data.data)),

  update: (id, data) =>
    instanceRef.instance
      .put(`casos/${id}`, toSnakeKeys(data))
      .then((res) => res.data.data),

  delete: (id) =>
    instanceRef.instance.delete(`casos/${id}`).then((res) => res.data.data),

  // Comentarios
  getComentarios: (idCaso) =>
    instanceRef.instance
      .get(`casos/${idCaso}/comentarios`)
      .then((res) => transformKeys(res.data.data || [])),

  addComentario: (idCaso, comentario) =>
    instanceRef.instance
      .post(`casos/${idCaso}/comentarios`, { comentario })
      .then((res) => transformKeys(res.data.data)),

  // Workflow
  cambiarEstado: (id, data) =>
    instanceRef.instance
      .put(`casos/${id}/estado`, data)
      .then((res) => res.data.data),

  avanzarEtapa: (id, data) =>
    instanceRef.instance
      .put(`casos/${id}/avanzar-etapa`, data)
      .then((res) => res.data.data),

  asignarEncargado: (id, encargado, idPerito) =>
    instanceRef.instance
      .post(`casos/${id}/asignar-encargado`, {
        encargado,
        id_perito: idPerito
      })
      .then((res) => res.data.data),

  asignarTaller: (id, tallerData) =>
    instanceRef.instance
      .post(`casos/${id}/asignar-taller`, tallerData)
      .then((res) => res.data.data),

  // Estadísticas
  getEstadisticas: (filters = {}) =>
    instanceRef.instance
      .get('casos/estadisticas', { params: filters })
      .then((res) => res.data.data),

  // Items Mano de Obra
  getItemsManoObra: (idCaso) =>
    instanceRef.instance
      .get(`casos/${idCaso}/items-mano-obra`)
      .then((res) => transformKeys(res.data.data || [])),

  addItemManoObra: (idCaso, data) =>
    instanceRef.instance
      .post(`casos/${idCaso}/items-mano-obra`, data)
      .then((res) => transformKeys(res.data.data)),

  updateItemManoObra: (idCaso, idItem, data) =>
    instanceRef.instance
      .put(`casos/${idCaso}/items-mano-obra/${idItem}`, data)
      .then((res) => transformKeys(res.data.data)),

  aprobarItemManoObra: (idCaso, idItem, data) =>
    instanceRef.instance
      .put(`casos/${idCaso}/items-mano-obra/${idItem}/aprobar`, data)
      .then((res) => res.data.data),

  rechazarItemManoObra: (idCaso, idItem, data) =>
    instanceRef.instance
      .put(`casos/${idCaso}/items-mano-obra/${idItem}/rechazar`, data)
      .then((res) => res.data.data),

  deleteItemManoObra: (idCaso, idItem) =>
    instanceRef.instance
      .delete(`casos/${idCaso}/items-mano-obra/${idItem}`)
      .then((res) => res.data.data),

  // Items Repuesto
  getItemsRepuesto: (idCaso) =>
    instanceRef.instance
      .get(`casos/${idCaso}/items-repuesto`)
      .then((res) => transformKeys(res.data.data || [])),

  addItemRepuesto: (idCaso, data) =>
    instanceRef.instance
      .post(`casos/${idCaso}/items-repuesto`, data)
      .then((res) => transformKeys(res.data.data)),

  updateItemRepuesto: (idCaso, idItem, data) =>
    instanceRef.instance
      .put(`casos/${idCaso}/items-repuesto/${idItem}`, data)
      .then((res) => transformKeys(res.data.data)),

  aprobarItemRepuesto: (idCaso, idItem, data) =>
    instanceRef.instance
      .put(`casos/${idCaso}/items-repuesto/${idItem}/aprobar`, data)
      .then((res) => res.data.data),

  rechazarItemRepuesto: (idCaso, idItem, data) =>
    instanceRef.instance
      .put(`casos/${idCaso}/items-repuesto/${idItem}/rechazar`, data)
      .then((res) => res.data.data),

  deleteItemRepuesto: (idCaso, idItem) =>
    instanceRef.instance
      .delete(`casos/${idCaso}/items-repuesto/${idItem}`)
      .then((res) => res.data.data),

  // Actualizar estado de entrega de repuestos
  updateRepuestosEntregados: (idCaso, items) =>
    instanceRef.instance
      .put(`casos/${idCaso}/repuestos-entregados`, { items })
      .then((res) => res.data.data),

  // Cargar presupuesto consolidado (fotos + items + licitación en un solo request)
  cargarPresupuesto: (idCaso, files, data) => {
    const formData = new FormData();
    if (files && files.length > 0) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    }
    formData.append('data', JSON.stringify(toSnakeKeys(data)));
    return instanceRef.instance
      .post(`casos/${idCaso}/cargar-presupuesto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => transformKeys(res.data.data));
  },

  // Retry Sebaot
  retrySebaotFotos: (idCaso) =>
    instanceRef.instance
      .post(`casos/${idCaso}/retry-sebaot-fotos`)
      .then((res) => res.data.data),

  retrySebaotOrdenes: (idCaso) =>
    instanceRef.instance
      .post(`casos/${idCaso}/retry-sebaot-ordenes`)
      .then((res) => res.data.data),

  getDatosPDF: (idCaso, idOrden = null) =>
    instanceRef.instance
      .get(`casos/${idCaso}/datos-pdf`, { params: { id_orden: idOrden } })
      .then((res) => transformKeys(res.data.data))
};

// ========================
// Talleres
// ========================
export const talleresApi = {
  getAll: (filters = {}) =>
    instanceRef.instance
      .get('casos-talleres', { params: filters })
      .then((res) => (res.data.data || []).map(transformTaller)),

  getById: (id) =>
    instanceRef.instance
      .get(`casos-talleres/${id}`)
      .then((res) => transformTaller(res.data.data)),

  create: (data) =>
    instanceRef.instance
      .post('casos-talleres', toSnakeKeys(data))
      .then((res) => transformTaller(res.data.data)),

  update: (id, data) =>
    instanceRef.instance
      .put(`casos-talleres/${id}`, toSnakeKeys(data))
      .then((res) => res.data.data),

  delete: (id) =>
    instanceRef.instance
      .delete(`casos-talleres/${id}`)
      .then((res) => res.data.data),

  // Especialidades
  addEspecialidad: (id, especialidad) =>
    instanceRef.instance
      .post(`casos-talleres/${id}/especialidades`, { especialidad })
      .then((res) => res.data.data),

  deleteEspecialidad: (id, especialidad) =>
    instanceRef.instance
      .delete(`casos-talleres/${id}/especialidades`, {
        data: { especialidad }
      })
      .then((res) => res.data.data),

  // Certificaciones
  addCertificacion: (id, data) =>
    instanceRef.instance
      .post(`casos-talleres/${id}/certificaciones`, data)
      .then((res) => res.data.data),

  deleteCertificacion: (id, idCert) =>
    instanceRef.instance
      .delete(`casos-talleres/${id}/certificaciones/${idCert}`)
      .then((res) => res.data.data),

  // Casos del taller
  getCasos: (id) =>
    instanceRef.instance
      .get(`casos-talleres/${id}/casos`)
      .then((res) => (res.data.data || []).map(transformCaso))
};

// ========================
// Proveedores
// ========================
export const proveedoresApi = {
  getAll: (filters = {}) =>
    instanceRef.instance
      .get('casos-proveedores', { params: filters })
      .then((res) => (res.data.data || []).map(transformProveedor)),

  getById: (id) =>
    instanceRef.instance
      .get(`casos-proveedores/${id}`)
      .then((res) => transformProveedor(res.data.data)),

  create: (data) =>
    instanceRef.instance
      .post('casos-proveedores', toSnakeKeys(data))
      .then((res) => transformProveedor(res.data.data)),

  update: (id, data) =>
    instanceRef.instance
      .put(`casos-proveedores/${id}`, toSnakeKeys(data))
      .then((res) => res.data.data),

  delete: (id) =>
    instanceRef.instance
      .delete(`casos-proveedores/${id}`)
      .then((res) => res.data.data),

  // Tipos de repuesto
  addTipoRepuesto: (id, tipoRepuesto) =>
    instanceRef.instance
      .post(`casos-proveedores/${id}/tipos-repuesto`, {
        tipo_repuesto: tipoRepuesto
      })
      .then((res) => res.data.data),

  deleteTipoRepuesto: (id, tipoRepuesto) =>
    instanceRef.instance
      .delete(`casos-proveedores/${id}/tipos-repuesto`, {
        data: { tipo_repuesto: tipoRepuesto }
      })
      .then((res) => res.data.data),

  // Marcas
  addMarca: (id, marca) =>
    instanceRef.instance
      .post(`casos-proveedores/${id}/marcas`, { marca })
      .then((res) => res.data.data),

  deleteMarca: (id, marca) =>
    instanceRef.instance
      .delete(`casos-proveedores/${id}/marcas`, { data: { marca } })
      .then((res) => res.data.data),

  // Métodos de pago
  addMetodoPago: (id, metodoPago) =>
    instanceRef.instance
      .post(`casos-proveedores/${id}/metodos-pago`, {
        metodo_pago: metodoPago
      })
      .then((res) => res.data.data),

  deleteMetodoPago: (id, metodoPago) =>
    instanceRef.instance
      .delete(`casos-proveedores/${id}/metodos-pago`, {
        data: { metodo_pago: metodoPago }
      })
      .then((res) => res.data.data),

  // Licitaciones del proveedor
  getLicitaciones: (id) =>
    instanceRef.instance
      .get(`casos-proveedores/${id}/licitaciones`)
      .then((res) => transformKeys(res.data.data || []))
};

// ========================
// Licitaciones
// ========================
export const licitacionesApi = {
  getAll: (filters = {}) =>
    instanceRef.instance
      .get('casos-licitaciones', { params: filters })
      .then((res) => transformKeys(res.data.data || [])),

  getById: (id) =>
    instanceRef.instance
      .get(`casos-licitaciones/${id}`)
      .then((res) => transformKeys(res.data.data)),

  create: (data) =>
    instanceRef.instance
      .post('casos-licitaciones', data)
      .then((res) => transformKeys(res.data.data)),

  update: (id, data) =>
    instanceRef.instance
      .put(`casos-licitaciones/${id}`, data)
      .then((res) => res.data.data),

  delete: (id) =>
    instanceRef.instance
      .delete(`casos-licitaciones/${id}`)
      .then((res) => res.data.data),

  updateItem: (licitacionId, itemId, data) =>
    instanceRef.instance
      .put(`casos-licitaciones/${licitacionId}/items/${itemId}`, data)
      .then((res) => res.data.data),

  aprobar: (id, data) =>
    instanceRef.instance
      .put(`casos-licitaciones/${id}/aprobar`, data)
      .then((res) => res.data.data)
};

// ========================
// Cotizaciones de proveedores (detalle de licitaciones)
// ========================
export const cotizacionesApi = {
  getAll: (filters = {}) =>
    instanceRef.instance
      .get('casos-cotizaciones', { params: filters })
      .then((res) => transformKeys(res.data.data || [])),

  getById: (id) =>
    instanceRef.instance
      .get(`casos-cotizaciones/${id}`)
      .then((res) => transformKeys(res.data.data)),

  enviar: (data) =>
    instanceRef.instance
      .post('casos-cotizaciones', data)
      .then((res) => transformKeys(res.data.data)),

  cargarRepresentante: (data) =>
    instanceRef.instance
      .post('casos-cotizaciones/representante', data)
      .then((res) => transformKeys(res.data.data)),

  marcarSeleccionados: (cotizacionItemIds) =>
    instanceRef.instance
      .put('casos-cotizaciones/marcar-seleccionados', {
        cotizacion_item_ids: cotizacionItemIds
      })
      .then((res) => res.data.data)
};

// ========================
// Órdenes
// ========================
export const ordenesApi = {
  getAll: (filters = {}) =>
    instanceRef.instance
      .get('casos-ordenes', { params: filters })
      .then((res) => transformKeys(res.data.data || [])),

  getById: (id) =>
    instanceRef.instance
      .get(`casos-ordenes/${id}`)
      .then((res) => transformKeys(res.data.data)),

  create: (data) =>
    instanceRef.instance
      .post('casos-ordenes', data)
      .then((res) => transformKeys(res.data.data)),

  update: (id, data) =>
    instanceRef.instance
      .put(`casos-ordenes/${id}`, data)
      .then((res) => res.data.data),

  delete: (id) =>
    instanceRef.instance
      .delete(`casos-ordenes/${id}`)
      .then((res) => res.data.data),

  anular: (id, data = {}) =>
    instanceRef.instance
      .post(`casos-ordenes/${id}/anular`, data)
      .then((res) => res.data.data),

  eliminarItem: (id, idItem, data = {}) =>
    instanceRef.instance
      .post(`casos-ordenes/${id}/eliminar-item`, {
        idItem,
        esAdicional: true,
        ...data
      })
      .then((res) => transformKeys(res.data.data)),

  editar: (id, data) =>
    instanceRef.instance
      .post(`casos-ordenes/${id}/editar`, { ...data, esAdicional: true })
      .then((res) => transformKeys(res.data.data)),

  editarMontos: (id, data) =>
    instanceRef.instance
      .post(`casos-ordenes/${id}/editar-montos`, data)
      .then((res) => transformKeys(res.data.data)),

  aprobarMO: (id, data) =>
    instanceRef.instance
      .post(`casos-ordenes/${id}/aprobar-mo`, { ...data, esAdicional: true })
      .then((res) => transformKeys(res.data.data)),

  aprobarRepuestos: (id, data) =>
    instanceRef.instance
      .post(`casos-ordenes/${id}/aprobar-repuestos`, {
        ...data,
        esAdicional: true
      })
      .then((res) => transformKeys(res.data.data)),

  generarDesdeAprobacion: (idLicitacion) =>
    instanceRef.instance
      .post('casos-ordenes/generar-desde-aprobacion', {
        id_licitacion: idLicitacion
      })
      .then((res) => transformKeys(res.data.data)),

  crearDesdeAprobacion: (data) =>
    instanceRef.instance
      .post('casos-ordenes/crear-desde-aprobacion', data)
      .then((res) => transformKeys(res.data.data)),

  // PDF de la orden (MO o Repuestos — el backend detecta el tipo).
  // Retorna un Blob listo para crear un object URL y abrirlo en nueva ventana.
  // Es el MISMO PDF que se adjunta al correo #5 / #6.
  getPdf: (id) =>
    instanceRef.instance
      .get(`casos-ordenes/${id}/pdf`, { responseType: 'blob' })
      .then((res) => res.data)
};

// ========================
// Permisos de Casos (PERITO)
// ========================
export const casosPermisosApi = {
  getAll: () =>
    instanceRef.instance
      .get('casos-permisos')
      .then((res) => transformKeys(res.data.data || [])),

  update: (idUsuario, data) =>
    instanceRef.instance
      .put(`casos-permisos/${idUsuario}`, toSnakeKeys(data))
      .then((res) => transformKeys(res.data.data)),

  getHistorial: (idUsuario) =>
    instanceRef.instance
      .get(`casos-permisos/${idUsuario}/historial`)
      .then((res) => transformKeys(res.data.data || [])),

  getPeritosDisponibles: () =>
    instanceRef.instance
      .get('casos/peritos-disponibles')
      .then((res) => transformKeys(res.data.data || []))
};

// ========================
// Archivos de Casos
// ========================
export const casosArchivosApi = {
  // Subir archivos a un caso
  upload: (idCaso, files, metadata = {}) => {
    const formData = new FormData();

    // Agregar archivos
    if (Array.isArray(files)) {
      files.forEach((file) => {
        formData.append('files', file);
      });
    } else {
      formData.append('files', files);
    }

    // Agregar metadata
    Object.keys(metadata).forEach((key) => {
      if (metadata[key] !== undefined && metadata[key] !== null) {
        if (typeof metadata[key] === 'object') {
          formData.append(key, JSON.stringify(metadata[key]));
        } else {
          formData.append(key, metadata[key]);
        }
      }
    });

    return instanceRef.instance
      .post(`casos/${idCaso}/archivos`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      .then((res) => transformKeys(res.data.data || []));
  },

  // Obtener archivos de un caso
  getByIdCaso: (idCaso, params = {}) =>
    instanceRef.instance
      .get(`casos/${idCaso}/archivos`, { params })
      .then((res) => transformKeys(res.data.data || []))
};

// ========================
// Catálogos
// ========================
export const catalogosApi = {
  getEspecialidades: () =>
    instanceRef.instance
      .get('casos-catalogos/especialidades')
      .then((res) => res.data.data || []),

  getCertificaciones: () =>
    instanceRef.instance
      .get('casos-catalogos/certificaciones')
      .then((res) => res.data.data || []),

  getTiposTrabajo: () =>
    instanceRef.instance
      .get('casos-catalogos/tipos-trabajo')
      .then((res) => res.data.data || []),

  getTiposCobertura: () =>
    instanceRef.instance
      .get('casos-catalogos/tipos-cobertura')
      .then((res) => res.data.data || [])
};
